import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Selection } from '@atlaskit/editor-prosemirror/state';
import {
	DEFAULT_BLOCK_LINK_HASH_PREFIX,
	extractBlockIdFromLinkHash,
	expandAllParentsThenScroll,
	getLocalIdSelector,
} from '@atlaskit/editor-common/block-menu';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { ExtractPresetAPI } from '@atlaskit/editor-common/preset';

import { fg } from '@confluence/feature-gating';
import { getHeadingIdsToElements } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';
export type EditorPresetAPI = ExtractPresetAPI<any>;

export const handleSelectionOnSyncedBlock = (
	editorAPI: EditorPresetAPI | undefined,
	blockId: string,
) => {
	if (!editorAPI?.core?.actions?.execute) {
		return;
	}

	// Catch the following potential errors when trying to set selection on the synced block:
	// tr.doc.resolve(...) - Can throw throw a RangeError
	// tr.setSelection(...) - Can throw
	try {
		editorAPI.core.actions.execute(({ tr }) => {
			const doc = tr.doc;

			// Find first top-level node matching the block ID and of type 'bodiedSyncBlock'.
			let syncedBlockPos = -1;

			doc.descendants((node, pos, parent) => {
				if (
					syncedBlockPos < 0 && // already found the block or not a top-level node, skip further checks
					parent?.type.name === 'doc' &&
					node.attrs?.localId === blockId &&
					node.type?.name === 'bodiedSyncBlock'
				) {
					syncedBlockPos = pos;
				}

				// Don't descend into this node's children
				// as synced blocks cannot be nested and we only want the first match at the top level.
				return false;
			});

			if (syncedBlockPos >= 0) {
				const safePos = Math.max(0, Math.min(syncedBlockPos + 1, tr.doc.content.size));
				// Set selection to the first position inside the synced block node.
				const $pos = tr.doc.resolve(safePos);
				editorAPI.core?.actions?.focus();
				tr.setSelection(Selection.near($pos));
				return tr;
			}

			return null;
		});
	} catch (_) {
		// Silently fail if selection cannot be set
	}
};

export const useScrollOnUrlChange = (
	hasProseMirrorRendered: boolean,
	blockLinkHashPrefix = DEFAULT_BLOCK_LINK_HASH_PREFIX,
	editorAPI?: EditorPresetAPI, // Editor API for setting selection on synced blocks
) => {
	const { hash } = useLocation();

	useEffect(() => {
		if (!hasProseMirrorRendered || !hash) {
			return;
		}

		// Store cleanup function to cancel pending operations on unmount or hash change.
		let cancelExpandAndScroll: (() => void) | null = null;
		let selectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

		// Handle block links when block menu feature is enabled.
		if (expValEquals('platform_editor_block_menu', 'isEnabled', true)) {
			const blockId = extractBlockIdFromLinkHash(hash, blockLinkHashPrefix);
			if (blockId) {
				const element = getLocalIdSelector(blockId, document.body);
				if (element) {
					// When ENABLED: Expand parent expands before scrolling.
					// When DISABLED: Simple scroll without expand handling (OLD BEHAVIOR for safe fallback).
					if (expValEquals('platform_editor_expand_on_scroll_to_block', 'isEnabled', true)) {
						// NEW BEHAVIOR: Handle block links with expand support.
						// Capture cleanup function to cancel pending timeouts on unmount/hash change.
						cancelExpandAndScroll = expandAllParentsThenScroll(element);
					} else {
						// OLD BEHAVIOR: Simple scroll without expand handling.
						element.scrollIntoView();
					}

					// Set selection on synced block if editor API is available
					// Small delay to ensure scroll/expand completes before setting selection
					if (
						expValEquals('platform_synced_block', 'isEnabled', true) &&
						fg('platform_synced_block_patch_2')
					) {
						selectionTimeoutId = setTimeout(() => {
							handleSelectionOnSyncedBlock(editorAPI, blockId);
						}, 100); // Delay to ensure scroll completes
					}
				}
				// Return cleanup function to cancel pending operations.
				return () => {
					if (cancelExpandAndScroll) {
						cancelExpandAndScroll();
					}
					if (selectionTimeoutId) {
						clearTimeout(selectionTimeoutId);
					}
				};
			}
		}

		// Handle regular anchor links and heading links.
		const id = hash.slice(1);

		const anchorElement = document.getElementById(id);
		// Indicates anchor.
		if (anchorElement) {
			anchorElement.scrollIntoView();
		}

		const headingIdToElement = getHeadingIdsToElements();
		const scrollContainer = document.getElementsByClassName(
			'fabric-editor-popup-scroll-parent',
		)?.[0];

		// The top of the heading element.
		const headingTop = headingIdToElement.get(id)?.getBoundingClientRect()?.top;
		// The top of the scroll container.
		const scrollContainerTop = scrollContainer?.getBoundingClientRect()?.top;
		// The current scroll position.
		const offset = scrollContainer?.scrollTop;

		if (headingTop === undefined || scrollContainerTop === undefined || offset === undefined) {
			return;
		}

		// Subtract the scroll container position from the heading position to get the scroll distance
		// and add the offset to account for current scroll position.
		const scrollToY = headingTop - scrollContainerTop + offset;

		scrollContainer?.scrollTo({
			left: 0,
			top: scrollToY,
			behavior: 'smooth',
		});
	}, [hash, blockLinkHashPrefix, hasProseMirrorRendered, editorAPI]);
};
